import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Heart, Check } from "lucide-react";
import { useRegistry } from "@/context/RegistryContext";
import { DASHBOARD } from "@/constants/testIds";

const PRESETS = [25, 50, 100, 200];

export const ContributeModal = ({ open, onOpenChange }) => {
  const { contributeToFund } = useRegistry();
  const [amount, setAmount] = useState(50);
  const [giver, setGiver] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("form");

  useEffect(() => {
    if (open) {
      setState("form");
      setGiver("");
      setMessage("");
      setAmount(50);
    }
  }, [open]);

  const submit = () => {
    setState("processing");
    setTimeout(() => {
      contributeToFund(amount, giver, message);
      setState("success");
    }, 900);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={DASHBOARD.contributeModal}
        className="bg-[#FDFBF7] border-[#E5E1D8] rounded-3xl max-w-md p-8"
      >
        {state !== "success" ? (
          <>
            <DialogHeader>
              <span className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
                Care Fund · Simulated
              </span>
              <DialogTitle className="font-display text-3xl text-[#2D3732] mt-3 leading-tight">
                Chip in toward
                <br />
                <span className="italic text-[#D98A71]">her care.</span>
              </DialogTitle>
              <DialogDescription className="text-[#5C6B62] mt-3">
                Every dollar goes toward the wishlist she's built. Give any amount.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6">
              <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">
                Your name
              </label>
              <input
                type="text"
                value={giver}
                onChange={(e) => setGiver(e.target.value)}
                placeholder="e.g. Sarah & James"
                data-testid="contribute-giver-name"
                className="mt-3 w-full h-12 rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 focus:outline-none focus:border-[#D98A71] transition-colors placeholder:text-[#5C6B62]/50"
              />
            </div>

            <div className="mt-5">
              <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">
                Amount (NZD)
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setAmount(p)}
                    className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                      amount === p
                        ? "bg-[#2D3732] text-[#FDFBF7] border-[#2D3732]"
                        : "border-[#E5E1D8] text-[#2D3732] hover:border-[#2D3732]"
                    }`}
                  >
                    ${p}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                data-testid="contribute-amount"
                className="mt-4 w-full h-12 rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 focus:outline-none focus:border-[#D98A71] transition-colors"
              />
            </div>

            <div className="mt-5">
              <label className="text-xs tracking-[0.2em] uppercase text-[#5C6B62]">
                A short note (optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Thinking of you."
                rows={2}
                data-testid="contribute-note"
                className="mt-3 w-full rounded-2xl border border-[#E5E1D8] bg-white/60 px-4 py-3 focus:outline-none focus:border-[#D98A71] transition-colors placeholder:text-[#5C6B62]/50 resize-none"
              />
            </div>

            <button
              data-testid={DASHBOARD.contributeConfirm}
              disabled={state === "processing" || !amount || amount < 1}
              onClick={submit}
              className="mt-6 w-full rounded-full bg-[#D98A71] hover:bg-[#c67a63] text-[#FDFBF7] py-4 text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {state === "processing" ? (
                <>Processing…</>
              ) : (
                <>
                  <Heart size={16} strokeWidth={1.5} />
                  Contribute ${amount}
                </>
              )}
            </button>
            <p className="mt-3 text-center text-[11px] text-[#5C6B62]">
              Simulated checkout · no card is charged.
            </p>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#A3B19B]/20 border border-[#A3B19B] flex items-center justify-center">
              <Check size={28} strokeWidth={1.5} className="text-[#A3B19B]" />
            </div>
            <h3 className="font-display text-3xl text-[#2D3732] mt-6 leading-tight">
              Held.
              <br />
              <span className="italic text-[#A3B19B]">Thank you.</span>
            </h3>
            <p className="mt-4 text-[#5C6B62] leading-relaxed">
              Your ${amount} will go toward her care fund. She'll know it came
              from{" "}
              <span className="text-[#2D3732]">
                {(giver || "a dear friend").trim() || "a dear friend"}
              </span>
              .
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-8 rounded-full border border-[#2D3732] text-[#2D3732] hover:bg-[#2D3732] hover:text-[#FDFBF7] px-6 py-3 text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
